import React, { createContext, useContext, useMemo } from 'react';

/**
 * Derives which fields are required directly from a Yup validation schema so
 * that labels can render a "*" without every call site opting in manually.
 *
 * A field counts as required when its Yup description has `optional === false`
 * (set by `.required()` — including `.test(...).required()`).
 */
const RequiredFieldsContext = createContext(new Set());

// Walk a Yup `describe()` output, collecting the dot-paths of required fields.
// Handles nested objects (`.fields`) and arrays of objects (`.innerType.fields`).
const collect = (fields, prefix, out) => {
  Object.entries(fields || {}).forEach(([key, field]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (field?.optional === false) out.add(path);
    if (field?.fields) collect(field.fields, path, out);
    if (field?.innerType?.fields) collect(field.innerType.fields, path, out);
  });
};

const collectRequired = (schema) => {
  const out = new Set();
  if (!schema || typeof schema.describe !== 'function') return out;
  try {
    collect(schema.describe().fields, '', out);
  } catch {
    // A malformed / conditional schema shouldn't break rendering.
  }
  return out;
};

// Strip array indices so "investors[0].name" / "investors.0.name" match "investors.name".
const normalize = (name) =>
  String(name || '')
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter((seg) => seg !== '' && !/^\d+$/.test(seg))
    .join('.');

export const RequiredFieldsProvider = ({ schema, children }) => {
  const value = useMemo(() => collectRequired(schema), [schema]);
  return (
    <RequiredFieldsContext.Provider value={value}>
      {children}
    </RequiredFieldsContext.Provider>
  );
};

// Returns true when the named field is required by the current schema.
export const useIsRequired = (name) => {
  const set = useContext(RequiredFieldsContext);
  if (!name) return false;
  return set.has(normalize(name));
};

export default RequiredFieldsContext;
